import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { Link } from 'react-router-dom';
import styles from './Detail.module.css';


const ItemDetail = () => {
    const { isAuthenticated, companyId, permissions, logout, token } = useAuth();
    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }

    }, [isAuthenticated, companyId, token, logout]);

    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');

    const { id } = useParams();
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [formData, setFormData] = useState({
        name: '',
        gstRate: '',
        isSubmit: false,
    });

    const [newRecordData, setNewRecordData] = useState({
        CategoryId: id,
        CompanyId: companyId,
        Name: '',
        Rate: '',
        GSTRate: '',
        Description: '',
    });

    const [updatedData, setUpdateData] = useState({
        CategoryId: id,
        Name: '',
        Rate: '',
        GSTRate: '',
        Description: '',
    });


    const [data, setData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    useEffect(() => {
        fetchData('CategoryApi', `/CategoryDetails/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Category Details:', error);
            });

        fetchItemData();
    }, [id]);

    const fetchItemData = async () => {
        try {
            const response = await fetchData('ItemApi', `/${id}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Item', error);
        }
    };

    const openDetailModal = async (item) => {
        try {
            const response = await fetchData('ItemApi', `/ItemDetails/${item.Id}`, token);
            if (response.responseCode === '1111') {
                setUpdateData(response.response_data);
                setIsDetailModalOpen(true);
            }
        } catch (error) {
            console.error('Error fetching Item Details:', error);
        }
    };



    const toggleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    const toggleModalBranch = () => {
        setIsDetailModalOpen(false); // Step 2
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setNewRecordData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleUpdateInputChange = (event) => {
        const { name, value } = event.target;
        setUpdateData((prevDataItem) => ({
            ...prevDataItem,
            [name]: value,
        }));
    };



    const addRecord = async () => {
        newRecordData.CategoryId = id;
        newRecordData.CompanyId = companyId;
        if (newRecordData.Name === '' || newRecordData.Rate === '') {
            console.error('Invalid data. Please fill all required fields.');
            return;
        }

        try {
            await postData('ItemApi', newRecordData, token);
            setNewRecordData({
                CategoryId: id,
                Name: '',
                Rate: '',
                GSTRate: '',
                Description: '',
            });
            fetchItemData();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Item Create error:', error);
        }
    };

    const updateRecord = async (event) => {
        event.preventDefault();

        if (!updatedData) {
            console.error('No Item details selected.');
            return;
        }

        try {
            await updateData('ItemApi', `${updatedData.Id}`, updatedData, token);
            setUpdateData({
                CategoryId: id,
                Name: '',
                Rate: '',
                GSTRate: '',
                Description: '',
            });
            fetchItemData();
            setIsDetailModalOpen(false);
        } catch (error) {
            console.error('Error updating record:', error);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteData('CategoryApi', `${deleteItemId}`, token);
            fetchItemData();
            setDeleteItemId(null);
            setConfirmDialogOpen(false);
        } catch (error) {
            console.error('Error deleting record:', error);
        }
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Item Details</h1>
                <Link to="/ItemIndex" className={styles.backButton}>
                    ← Back to List
                </Link>
            </div>

            {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
            {successMessage && <div className={styles.successMessage}>{successMessage}</div>}

            <div className={styles.contentGrid}>
                <div className={styles.categoryCard}>
                    <div className={styles.cardHeader}>
                        <h2 className={styles.cardTitle}>📦 Category Information</h2>
                    </div>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.label} htmlFor="Name">
                                🏷️ Item Name
                            </label>
                            <input
                                className={styles.input}
                                name="Name"
                                value={formData.name}
                                type="text"
                                placeholder="Enter Item Name"
                                readOnly
                            />
                        </div>
                        <div className={styles.formGroupSmall}>
                            <label className={styles.label} htmlFor="rate">
                                💰 GST %
                            </label>
                            <input
                                className={`${styles.input} ${styles.inputRight}`}
                                name="GSTRate"
                                value={formData.gstRate}
                                type="number"
                                placeholder="GST %"
                                readOnly
                            />
                        </div>
                    </div>
                    <div className={styles.categoryStats}>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber}>{data.length}</span>
                            <span className={styles.statLabel}>Total Items</span>
                        </div>
                        <div className={styles.statItem}>
                            <span className={styles.statNumber}>
                                ₹{data.reduce((sum, item) => sum + parseFloat(item.Rate || 0), 0).toFixed(2)}
                            </span>
                            <span className={styles.statLabel}>Total Value</span>
                        </div>
                    </div>
                </div>

                <div className={styles.itemsCard}>
                    <div className={styles.cardHeader}>
                        <h2 className={styles.cardTitle}>📋 Item Management</h2>
                        {hasCreateMasterPermission && (
                            <button className={styles.addButton} onClick={toggleModal}>
                                ✨ Add New Item
                            </button>
                        )}
                    </div>



                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead className={styles.tableHeader}>
                                <tr>
                                    <th className={styles.tableHeaderCell}>🏷️ Name</th>
                                    <th className={styles.tableHeaderCell}>💰 Rate</th>
                                    <th className={styles.tableHeaderCell}>⚡ Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data && data.length > 0 ? (
                                    data.map(item => (
                                        <tr key={item.Id} className={styles.tableRow}>
                                            <td className={styles.tableCell}>{item.Name}</td>
                                            <td className={styles.tableCell}>₹{item.Rate}</td>
                                            <td className={styles.tableCell}>
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={() => openDetailModal(item)}
                                                    title="Edit Item"
                                                >
                                                    <ArrowForward style={{ color: '#fbbf24', fontSize: '14px' }} />
                                                </button>
                                                {hasDeleteMasterPermission && (
                                                    <button
                                                        className={styles.actionButton}
                                                        onClick={() => openConfirmDialog(item.Id)}
                                                        title="Delete Item"
                                                    >
                                                        <DeleteIcon style={{ color: '#ef4444', fontSize: '14px' }} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className={styles.tableCell}>
                                            <div className={styles.emptyState}>
                                                <div className={styles.emptyStateIcon}>📦</div>
                                                <div className={styles.emptyStateText}>No items found</div>
                                                <div className={styles.emptyStateSubtext}>
                                                    {hasCreateMasterPermission ?
                                                        "Click 'Add New Item' to get started" :
                                                        "No items available in this category"
                                                    }
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                </div>
            </div>

            {/* Delete confirmation dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Record?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <button onClick={closeConfirmDialog} className={styles.cancelButton}>
                        Cancel
                    </button>
                    <button onClick={handleDelete} className={styles.confirmButton}>
                        Confirm
                    </button>
                </DialogActions>
            </Dialog>

            {isDetailModalOpen && (
                <div className={styles.modal} onClick={(e) => e.target === e.currentTarget && toggleModalBranch()}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>
                                <span>✏️</span>
                                Edit Item
                            </h3>
                            <button className={styles.modalCloseButton} onClick={toggleModalBranch}>
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.modalFormGroup}>
                                <label className={styles.modalLabel} htmlFor="name">
                                    <span className={styles.labelIcon}>🏷️</span>
                                    Item Name
                                </label>
                                <input
                                    className={styles.modalInput}
                                    type="text"
                                    name="Name"
                                    placeholder="Enter item name..."
                                    value={updatedData.Name}
                                    onChange={handleUpdateInputChange}
                                />
                            </div>

                            <div className={styles.modalFormGroup}>
                                <label className={styles.modalLabel} htmlFor="Description">
                                    <span className={styles.labelIcon}>📝</span>
                                    Description
                                </label>
                                <textarea
                                    className={styles.modalTextarea}
                                    name="Description"
                                    placeholder="Describe your item in detail..."
                                    value={updatedData.Description}
                                    onChange={handleUpdateInputChange}
                                />
                            </div>

                            <div className={styles.modalFormRow}>
                                <div className={styles.modalFormGroup}>
                                    <label className={styles.modalLabel} htmlFor="Rate">
                                        <span className={styles.labelIcon}>💰</span>
                                        MRP (₹)
                                    </label>
                                    <input
                                        className={styles.modalInput}
                                        type="number"
                                        name="Rate"
                                        placeholder="0.00"
                                        value={updatedData.Rate}
                                        onChange={handleUpdateInputChange}
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                                <div className={styles.modalFormGroupSmall}>
                                    <label className={styles.modalLabel} htmlFor="GSTRate">
                                        <span className={styles.labelIcon}>📊</span>
                                        GST %
                                    </label>
                                    <input
                                        className={styles.modalInput}
                                        type="number"
                                        name="GSTRate"
                                        placeholder="0"
                                        value={updatedData.GSTRate}
                                        onChange={handleUpdateInputChange}
                                        step="0.1"
                                        min="0"
                                        max="100"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.modalActions}>
                            {hasEditMasterPermission && (
                                <button className={styles.primaryButton} onClick={updateRecord}>
                                    <span>💾</span>
                                    Update Item
                                </button>
                            )}
                            <button className={styles.secondaryButton} onClick={toggleModalBranch}>
                                <span>✕</span>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isModalOpen && (
                <div className={styles.modal} onClick={(e) => e.target === e.currentTarget && toggleModal()}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>
                                <span>✨</span>
                                Add New Item
                            </h3>
                            <button className={styles.modalCloseButton} onClick={toggleModal}>
                                ×
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.modalFormGroup}>
                                <label className={styles.modalLabel} htmlFor="name">
                                    <span className={styles.labelIcon}>🏷️</span>
                                    Item Name
                                </label>
                                <input
                                    className={styles.modalInput}
                                    type="text"
                                    name="Name"
                                    placeholder="Enter item name..."
                                    value={newRecordData.Name}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className={styles.modalFormGroup}>
                                <label className={styles.modalLabel} htmlFor="Description">
                                    <span className={styles.labelIcon}>📝</span>
                                    Description
                                </label>
                                <textarea
                                    className={styles.modalTextarea}
                                    name="Description"
                                    placeholder="Describe your item in detail..."
                                    value={newRecordData.Description}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className={styles.modalFormRow}>
                                <div className={styles.modalFormGroup}>
                                    <label className={styles.modalLabel} htmlFor="Rate">
                                        <span className={styles.labelIcon}>💰</span>
                                        MRP (₹)
                                    </label>
                                    <input
                                        className={styles.modalInput}
                                        type="number"
                                        name="Rate"
                                        placeholder="0.00"
                                        value={newRecordData.Rate}
                                        onChange={handleInputChange}
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                                <div className={styles.modalFormGroupSmall}>
                                    <label className={styles.modalLabel} htmlFor="GSTRate">
                                        <span className={styles.labelIcon}>📊</span>
                                        GST %
                                    </label>
                                    <input
                                        className={styles.modalInput}
                                        type="number"
                                        name="GSTRate"
                                        placeholder="0"
                                        value={newRecordData.GSTRate}
                                        onChange={handleInputChange}
                                        step="0.1"
                                        min="0"
                                        max="100"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={styles.modalActions}>
                            <button className={styles.primaryButton} onClick={addRecord}>
                                <span>✨</span>
                                Add Item
                            </button>
                            <button className={styles.secondaryButton} onClick={toggleModal}>
                                <span>✕</span>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ItemDetail;
